import {
  AI_MESSAGE_SENDER,
  Chat,
  CLIENT_MESSAGE_SENDER,
  GptModel,
  NEW_CHAT_ID,
  Prompt,
  PromptExtension,
  ResolvedChatMessage,
  TOOL_CALL_USER_RESULT_OK,
  TOOL_CALL_USER_RESULT_REJECT,
} from '../../types';
import { MessageList } from './MessageList';
import { ComplexContent, stringToComplexContent, TextMessageInput } from './TextMessageInput';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ENV_E2E, growegyAIConfig, readCommonConfig } from '../../../commons/config';
import { LibraryPromptWizardState, useChatContext } from '../chatContext';
import { getTempMessageId, NUMBER_PLACEHOLDER } from '../../util';
import { useQueryCacheManager } from '../../../hooks/useQueryCacheManager';
import { VariableMessage } from './VariableMessage';
import { MessageListBottomPlaceholder } from './MessageListBottomPlaceholder';
import { useCancelMessageMutation } from '../hooks/useCancelMessageMutation';
import { MutationStatus } from '@tanstack/react-query';
import { GptModelSelect } from './GptModelSelect';
import { createPortal } from 'react-dom';
import { ExecuteToolsMessageInput } from './ExecuteToolsMessageInput';
import { useResolvedChatMessages } from '../hooks/useResolvedChatMessages';
import { SendMessageSource, useSendMessageMutation } from '../hooks/useSendMessageMutation';

const SMOOTH_SCROLL_SUPPORTED = readCommonConfig().env !== ENV_E2E;
const MAX_OPEN_AI_RESPONSES_LIMIT = growegyAIConfig.maxMessagesPerChat;
const MAX_CLIENT_REQUESTS_LIMIT = growegyAIConfig.maxMessagesPerChat * 2; // in case of network issues or other errors number of requests can be higher than number of responses

export const Messenger = (props: {
  chat: Chat;
  hideIntro?: boolean;
  systemMessage?: string | (() => string) | null;
  textInputPlaceholder?: string;
  slimGptModelSelect?: boolean;
  gptModelSelectDiv?: HTMLDivElement | null;
  onChatCreated: (chat: Chat) => void;
  onOpenLibraryClick?: () => void;
  onNewChatClick: () => void;
  onManageVariablesClick?: () => void;
  onPromptClick?: (prompt: Prompt) => void;
  onToolCallProceed?: () => void;
}) => {
  const {
    chat,
    hideIntro,
    systemMessage,
    textInputPlaceholder,
    slimGptModelSelect,
    gptModelSelectDiv,
    onChatCreated,
    onOpenLibraryClick,
    onNewChatClick,
    onManageVariablesClick,
    onPromptClick,
    onToolCallProceed,
  } = props;
  const queryCacheManager = useQueryCacheManager();

  const lastMessageRef = useRef<HTMLDivElement | null>(null);
  const scrollToBottom = useCallback((scrollStyle: 'smooth' | 'auto') => {
    const current = lastMessageRef.current;
    if (!current) return;
    const behavior = SMOOTH_SCROLL_SUPPORTED ? scrollStyle : 'auto';
    if (behavior === 'smooth')
      setTimeout(() => current.scrollIntoView({ behavior: behavior }), 100);
    else current.scrollIntoView({ behavior: behavior });
  }, []);

  const { mutate: cancelMessageMutation, status: cancelMessageStatus } = useCancelMessageMutation(
    () => {
      setSendMessageCancelled(true);
    },
    () => {
      setSendMessageCancelled(false);
    }
  );

  const { getChatContext, setChatContext, getCurrentModel, setCurrentModel } = useChatContext();
  const currentModel = getCurrentModel();
  const [gptModel, setGptModel] = useState<GptModel>(currentModel);
  const [complexContent, setComplexContent] = useState<ComplexContent>(() => {
    const chatContext = getChatContext(chat.id);
    return { rawContent: chatContext.rawContent, plainTextContent: chatContext.plainTextContent };
  });
  useEffect(() => {
    setChatContext(chat.id, {
      rawContent: complexContent.rawContent,
      plainTextContent: complexContent.plainTextContent,
    });
  }, [chat.id, complexContent, setChatContext]);

  const [libraryPromptWizardState, setLibraryPromptWizardState] =
    useState<LibraryPromptWizardState | null>(getChatContext(chat.id).libraryPromptWizardState);

  const [sendMessageCancelled, setSendMessageCancelled] = useState(false);
  const [needToScroll, setNeedToScroll] = useState(false);
  const {
    query: { data: messages, status: messagesStatus },
    queryStatusComponent: messagesQueryStatusComponent,
  } = useResolvedChatMessages(chat.id, !!libraryPromptWizardState);
  useEffect(() => {
    if (needToScroll) {
      scrollToBottom('smooth');
      setNeedToScroll(false);
    }
  }, [messages, needToScroll, scrollToBottom]);

  const {
    mutate: sendMessageMutation,
    status: sendMessageStatus,
    variables: sendMessageVariables,
  } = useSendMessageMutation({
    onChatCreated,
    onChatCreationFailed: ({ firstMessageContent }) => setComplexContent(firstMessageContent),
    onMessageCreated: () => setNeedToScroll(true),
    gptModel,
    systemMessage,
    setComplexContent,
    libraryPromptWizardState,
    setLibraryPromptWizardState,
  });

  const sendMessage = useCallback(
    (args: {
      contentToSend: ComplexContent | null;
      source: SendMessageSource;
      promptExtensions?: PromptExtension[];
    }) => {
      setSendMessageCancelled(false);
      sendMessageMutation({ ...args, chat, messageClientId: getTempMessageId() });
    },
    [chat, sendMessageMutation]
  );

  useEffect(() => {
    if (messages && messages.length === 0 && libraryPromptWizardState) {
      sendMessage({
        contentToSend: stringToComplexContent(libraryPromptWizardState.prompt.content),
        source: 'library',
      });
    }
  }, [messages, sendMessage, libraryPromptWizardState, gptModel]);

  const messagesSuccessfullyLoaded = messagesStatus === 'success';
  useEffect(() => {
    scrollToBottom('auto');
    setNeedToScroll(false);
  }, [chat.id, messagesSuccessfullyLoaded, scrollToBottom]);

  const onSendAgain = useCallback(
    (message: ResolvedChatMessage) => {
      queryCacheManager.onMessageDeleted(chat.id, message);
      sendMessage({
        contentToSend: message.content ? stringToComplexContent(message.content) : null,
        source: 'retry',
      });
    },
    [chat.id, sendMessage, queryCacheManager]
  );

  const currentWizardStep =
    libraryPromptWizardState &&
    libraryPromptWizardState.currentStepState.index > 0 &&
    libraryPromptWizardState.currentStepState.index <= libraryPromptWizardState.steps.length
      ? libraryPromptWizardState.steps[libraryPromptWizardState.currentStepState.index - 1]
      : null;

  const showVariableMessage =
    currentWizardStep &&
    !currentWizardStep.isFreeForm &&
    ((currentWizardStep.valueOptions !== null && currentWizardStep.valueOptions.length > 0) ||
      (currentWizardStep.variableName && currentWizardStep.variableName !== NUMBER_PLACEHOLDER));
  const awaitingNextWizardStep = libraryPromptWizardState && sendMessageStatus === 'loading';

  const lastMessageResolvedToolCalls =
    messages &&
    messages.length > 0 &&
    (messages[messages.length - 1].resolvedToolCalls?.length ?? 0) > 0
      ? messages[messages.length - 1].resolvedToolCalls
      : null;

  const {
    adjustedCreateMessageStatus,
    sendingClientMessageId,
  }: { adjustedCreateMessageStatus: MutationStatus; sendingClientMessageId: string | null } =
    sendMessageCancelled
      ? { adjustedCreateMessageStatus: 'idle', sendingClientMessageId: null }
      : messages && messages.length > 0 && messages[messages.length - 1].isPlaceholder
      ? {
          adjustedCreateMessageStatus: 'loading',
          sendingClientMessageId: messages[messages.length - 1].clientId,
        }
      : {
          adjustedCreateMessageStatus: sendMessageStatus,
          sendingClientMessageId: sendMessageVariables
            ? sendMessageVariables.messageClientId
            : null,
        };

  return (
    <>
      {gptModelSelectDiv &&
        createPortal(
          <GptModelSelect
            gptModel={chat.id === NEW_CHAT_ID ? gptModel : chat.model}
            disabled={chat.id !== NEW_CHAT_ID || sendMessageStatus === 'loading'}
            slim={slimGptModelSelect}
            onGptModelChanged={(model) => {
              setCurrentModel(model);
              setGptModel(model);
            }}
          />,
          gptModelSelectDiv
        )}
      <div className="messenger__container" style={{ scrollBehavior: 'smooth' }}>
        {messages ? (
          <>
            <div className="messenger__message-list">
              <div className="flex-grow-1" />

              <MessageList
                ref={lastMessageRef}
                chatType={chat.type}
                messages={messages}
                maxAiResponses={MAX_OPEN_AI_RESPONSES_LIMIT}
                hideIntro={hideIntro}
                onIntroMessageSend={(x) =>
                  sendMessage({
                    contentToSend: stringToComplexContent(x),
                    source: 'sample',
                  })
                }
                onPromptClick={onPromptClick}
                onSendAgain={onSendAgain}
                onOpenLibraryClick={onOpenLibraryClick}
                onNewChatClick={onNewChatClick}
              />
              {(!showVariableMessage || awaitingNextWizardStep) && <MessageListBottomPlaceholder />}
            </div>
            {!awaitingNextWizardStep &&
              (showVariableMessage ? (
                <VariableMessage
                  key={`step-${libraryPromptWizardState?.currentStepState.index}`}
                  chatId={chat.id}
                  variableName={currentWizardStep.variableName}
                  predefinedValues={currentWizardStep.valueOptions}
                  metadata={currentWizardStep.metadata}
                  onSend={(content) =>
                    sendMessage({
                      contentToSend: stringToComplexContent(content),
                      source: 'composer',
                    })
                  }
                />
              ) : lastMessageResolvedToolCalls && lastMessageResolvedToolCalls.length > 0 ? (
                <ExecuteToolsMessageInput
                  onModify={() =>
                    sendMessage({
                      contentToSend: stringToComplexContent('Modify'),
                      source: 'function',
                      promptExtensions: lastMessageResolvedToolCalls.map((x) => ({
                        toolCallId: x.id,
                        toolCallName: x.name,
                        userResult: TOOL_CALL_USER_RESULT_REJECT,
                      })),
                    })
                  }
                  onProceed={() => {
                    sendMessage({
                      contentToSend: stringToComplexContent('Proceed'),
                      source: 'function',
                      promptExtensions: lastMessageResolvedToolCalls.map((x) => ({
                        resolvedToolCall: x,
                        userResult: TOOL_CALL_USER_RESULT_OK,
                      })),
                    });
                    onToolCallProceed && onToolCallProceed();
                  }}
                />
              ) : (
                <TextMessageInput
                  chatId={chat.id}
                  chatType={chat.type}
                  complexContent={complexContent}
                  placeholder={textInputPlaceholder}
                  onChange={setComplexContent}
                  onSend={() =>
                    sendMessage({
                      contentToSend: complexContent,
                      source: 'input',
                    })
                  }
                  onCancelSending={() => {
                    if (
                      chat.id !== NEW_CHAT_ID &&
                      adjustedCreateMessageStatus === 'loading' &&
                      cancelMessageStatus !== 'loading' &&
                      !!sendingClientMessageId
                    ) {
                      cancelMessageMutation({
                        chatId: chat.id,
                        clientMessageId: sendingClientMessageId,
                      });
                    }
                  }}
                  onManageVariablesClick={onManageVariablesClick}
                  tooManyMessages={
                    messages.filter((m) => m.sender === AI_MESSAGE_SENDER && !m.isWizardStep)
                      .length >= MAX_OPEN_AI_RESPONSES_LIMIT ||
                    messages.filter((m) => m.sender === CLIENT_MESSAGE_SENDER && !m.isWizardStep)
                      .length >= MAX_CLIENT_REQUESTS_LIMIT
                  }
                  maxAiResponses={MAX_OPEN_AI_RESPONSES_LIMIT}
                  sendMessageStatus={adjustedCreateMessageStatus}
                  cancelMessageStatus={cancelMessageStatus}
                />
              ))}
          </>
        ) : (
          messagesQueryStatusComponent
        )}
      </div>
    </>
  );
};
